'use client'

import { clsx } from 'clsx'
import { useEffect } from 'react'
import { PriceLoader } from '@/components/PriceLoader/PriceLoader'
import { Skeleton } from '@/components/Skeleton/Skeleton'
import { OfferPresenterV2 } from '@/features/priceCalculator/PurchaseFormV2/OfferPresenterV2/OfferPresenterV2'
import { usePriceCalculatorStep } from '../usePriceCalculatorStep'
import { usePriceIntent } from '../usePriceIntent'
import { InsuranceDataForm } from './InsuranceDataForm/InsuranceDataForm'
import { centered, loadingSkeleton, priceLoaderWrapper, viewOffersWrapper } from './PurchaseFormV2.css'
import { PRELOADED_PRICE_INTENT_QUERY_PARAM, usePreloadedPriceIntentId } from './usePreloadedPriceIntentId'

export function PurchaseFormV2() {
  useWarnOnPreloadedPriceIntentId()

  const { priceIntent } = usePriceIntent()

  const { step, setStep } = usePriceCalculatorStep()

  useEffect(() => {
    // 1. Check if price intent is ready, otherwise the default step is 'loadingForm'
    // 2. Only setStep viewOffers if the form is loading to enable priceIntent
    // updates without going to step viewOffers before priceIntent is confirmed
    if (priceIntent && step === 'loadingForm') {
      if (priceIntent.offers.length) {
        setStep('viewOffers')
      } else {
        setStep('fillForm')
      }
    }
  }, [priceIntent, setStep, step])

  switch (step) {
    case 'loadingForm':
      return <Skeleton className={clsx(centered, loadingSkeleton)} />
    case 'fillForm':
      return <InsuranceDataForm className={centered} />
    case 'calculatingPrice':
      return (
        <div className={priceLoaderWrapper}>
          <PriceLoader />
        </div>
      )
    case 'viewOffers': {
      // The PurchaseFormV2 mounts and renders when clicking on the "add more insurance" button.
      // At that point, the step is 'viewOffers' and the priceIntent is empty.
      // The component renders once before the effect runs and sets the step to 'fillForm'.
      // We don't want to render the component in that case, so we return null.
      if (priceIntent?.offers.length === 0) {
        return null
      }

      return (
        <div className={viewOffersWrapper}>
          <InsuranceDataForm />
          <OfferPresenterV2 />
        </div>
      )
    }

    default:
      throw new Error(`Unexpected step: ${step}`)
  }
}

const useWarnOnPreloadedPriceIntentId = () => {
  const preloadedPriceIntentId = usePreloadedPriceIntentId()
  useEffect(() => {
    if (preloadedPriceIntentId != null) {
      console.warn(
        `preloadedPriceIntentId is not supported on PriceCalculatorPage, please find and update the code that sets ${PRELOADED_PRICE_INTENT_QUERY_PARAM} query param`,
      )
    }
  }, [preloadedPriceIntentId])
}
